
import React, { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider, useAuth, useAuthContext } from './context/AuthContext';
import { ModuleProvider } from './context/ModuleContext';
import { AuthPage } from './pages/AuthPage';
import { AuthCallback } from './pages/AuthCallback';
import { CompleteSignup } from './pages/CompleteSignup';
import { ParentDashboard } from './pages/parent/ParentDashboard';
import { MobPayDashboard } from './pages/mobpay/MobPayDashboard';
import { BusinessDashboard } from './pages/business/BusinessDashboard';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { PaymentReturn } from './pages/PaymentReturn';
import { SubscriptionPortal } from './pages/SubscriptionPortal';
import { GDPRConsentModal } from './components/GDPRConsentModal';
import { API } from './utils/api';
import axios from 'axios';
import { SUPABASE_CONFIG_ERROR } from './lib/supabase';

import { ForgotPassword } from './pages/ForgotPassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { ResetPassword } from './pages/ResetPassword';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';

import { Tools } from './pages/Tools';

import GuestPaymentPage from './pages/GuestPaymentPage';
import GuestPaymentReturn from './pages/GuestPaymentReturn';
import OwnerDashboardPrototype from './pages/prototype/OwnerDashboardPrototype';
import OwnerDashboard from './pages/owner/OwnerDashboard';
import WorkspacePage from './pages/workspace/WorkspacePage';
import { useIsMobile } from './hooks/useIsMobile';

function PendingApprovalScreen({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h2 className="text-xl font-bold text-amber-900">Pending Approval</h2>
        <p className="text-amber-700">
          Your Business account for <strong>{user?.business_info?.company_name || 'your company'}</strong> is under review.
        </p>
        <p className="text-sm text-amber-600">You will be notified by email when your account is approved.</p>
        <button onClick={onLogout} className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700" data-testid="pending-logout-btn">
          Sign Out
        </button>
      </div>
    </div>
  );
}

function RejectedScreen({ user, onLogout }) {
  return (
    <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </div>
        <h2 className="text-xl font-bold text-red-900">Account Not Approved</h2>
        <p className="text-red-700">
          Your Business account application was not approved at this time.
        </p>
        <p className="text-sm text-red-600">Please contact <a href="mailto:support@paymig.app" className="underline">support@paymig.app</a> for more information.</p>
        <button onClick={onLogout} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-testid="rejected-logout-btn">
          Sign Out
        </button>
      </div>
    </div>
  );
}

function Gate() {
  // Single hoisted auth instance: consume the App-level provider (above
  // BrowserRouter) instead of creating a second useAuth() instance.
  const auth = useAuthContext();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [showGDPRModal, setShowGDPRModal] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);

  // Check if user needs to complete their profile (no Swish)
  // This handles Google OAuth users who need to add Swish
  const isPayer = auth.user?.role === 'parent' || auth.user?.user_type === 'PERSONAL' || auth.user?.user_type === 'PAYER';
  const isBusiness = auth.user?.role === 'business' || auth.user?.user_type === 'BUSINESS';
  const isPendingApproval = auth.user?.account_status === 'PENDING_APPROVAL';
  const isRejected = auth.user?.account_status === 'REJECTED';

  // Post-login redirect (e.g., back to /pay/:paymigId after login)
  useEffect(() => {
    if (auth.user && auth.token) {
      const redirect = sessionStorage.getItem('post_login_redirect');
      if (redirect) {
        sessionStorage.removeItem('post_login_redirect');
        nav(redirect);
        return;
      }
      // Business accounts on desktop/tablet go straight to /owner (ERP)
      // On mobile they stay in the mobile /business dashboard.
      if (isBusiness && !isMobile && !isPendingApproval && !isRejected) {
        nav('/owner');
        return;
      }
      // Workspace context persistence: personal users return to their last-used context
      if (auth.user.user_type !== 'BUSINESS' && localStorage.getItem('paymig_ctx') === 'workspace') {
        nav('/workspace');
      }
    }
  }, [auth.user, auth.token, nav, isBusiness, isMobile, isPendingApproval, isRejected]);

  useEffect(() => {
    // Personal accounts without Swish need to complete profile
    // But don't redirect — show CompleteSignup inline (legacy path for page refreshes)
  }, [auth.user, isPayer]);

  // If user is Personal but has no Swish (incomplete profile from Google OAuth)
  const needsProfileCompletion = auth.user && isPayer && !auth.user.swish_payee_alias;

  // Check if user needs to accept GDPR consent
  const checkGDPRConsent = useCallback(async () => {
    if (!auth.user || !auth.token) {
      setGdprChecked(true);
      return;
    }

    try {
      const res = await axios.get(`${API}/gdpr/consents`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      
      const consents = res.data.consents || {};
      // Check if user has accepted required consents (terms + privacy)
      const hasAcceptedTerms = consents.terms_accepted === true;
      const hasAcceptedPrivacy = consents.privacy_accepted === true;
      
      if (!hasAcceptedTerms || !hasAcceptedPrivacy) {
        setShowGDPRModal(true);
      }
    } catch (e) {
      console.error('Failed to check GDPR consent:', e);
      // If we can't check, don't block the user (fail open for now)
    } finally {
      setGdprChecked(true);
    }
  }, [auth.user, auth.token]);

  useEffect(() => {
    if (auth.user && auth.token) {
      checkGDPRConsent();
    } else {
      setGdprChecked(true);
    }
  }, [auth.user, auth.token, checkGDPRConsent]);

  // Handle GDPR consent acceptance
  const handleGDPRAccept = async (consentData) => {
    try {
      await axios.post(`${API}/gdpr/accept-all`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      
      // If marketing was accepted, update that separately
      if (consentData.marketing) {
        await axios.post(`${API}/gdpr/consent`, {
          consent_type: 'marketing',
          accepted: true
        }, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
      }
      
      setShowGDPRModal(false);
    } catch (e) {
      console.error('Failed to record GDPR consent:', e);
      // Still close modal on error to not block user
      setShowGDPRModal(false);
    }
  };
  
  if (!auth.user) {
    return <AuthPage />;
  }

  // Show loading while checking GDPR status
  if (!gdprChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <ModuleProvider>
        {/* GDPR Consent Modal */}
        <GDPRConsentModal 
          open={showGDPRModal} 
          onAccept={handleGDPRAccept} 
        />
        
        {/* Main Dashboard - blocked if GDPR modal is showing */}
        {needsProfileCompletion ? (
          <CompleteSignup />
        ) : isPendingApproval ? (
          <PendingApprovalScreen user={auth.user} onLogout={auth.clear} />
        ) : isRejected ? (
          <RejectedScreen user={auth.user} onLogout={auth.clear} />
        ) : isBusiness ? <BusinessDashboard /> : isPayer ? <ParentDashboard /> : <MobPayDashboard />}
    </ModuleProvider>
  );
}

function MisconfigScreen({ message }) {
  return (
    <div className="min-h-screen bg-red-50 flex items-center justify-center p-6" data-testid="supabase-misconfig-screen">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center space-y-3">
        <h1 className="text-xl font-bold text-red-900">Configuration error</h1>
        <p className="text-sm text-red-700">{message}</p>
        <p className="text-xs text-muted-foreground">The app cannot start until the required Supabase build-time variables are provided.</p>
      </div>
    </div>
  );
}

function App() {
  const auth = useAuth();
  const recaptchaKey = window._env_?.REACT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (SUPABASE_CONFIG_ERROR) {
    return <MisconfigScreen message={SUPABASE_CONFIG_ERROR} />;
  }
  const appContent = (
    <AuthProvider value={auth}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );

  if (!recaptchaKey || recaptchaKey.includes('PLACEHOLDER')) {
    return appContent;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      {appContent}
    </GoogleReCaptchaProvider>
  );
}

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Defensive OAuth root-catcher: if the browser lands on the app host at the root
  // (or any non-callback path) carrying an OAuth `code`/`error` in the query string
  // — e.g. because a Supabase Redirect URL allow-list fell back to the default Site
  // URL — forward to /auth/callback (preserving ALL query params) so AuthCallback can
  // complete the exchange or show the error, instead of dropping the user on a landing page.
  const _params = new URLSearchParams(location.search);
  const hasOAuthReturn =
    (_params.has('code') || _params.has('error') || _params.has('error_description')) &&
    location.pathname !== '/auth/callback';

  useEffect(() => {
    if (hasOAuthReturn) {
      console.debug('[oauth-root-catcher] forwarding OAuth return to /auth/callback', location.search);
      navigate(`/auth/callback${location.search}`, { replace: true });
    }
  }, [hasOAuthReturn, location.search, navigate]);

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (hasOAuthReturn) {
    // Invisible marker so the redirect code path is assertable; navigate() above routes.
    return <div data-testid="oauth-root-catcher" style={{ display: 'none' }} />;
  }

  return (
    <Routes>
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/subscription-portal" element={<SubscriptionPortal />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment-return" element={<PaymentReturn />} />
      <Route path="/pay/:paymigId" element={<GuestPaymentPage />} />
      <Route path="/guest-payment-return" element={<GuestPaymentReturn />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/complete-signup" element={<CompleteSignup />} />
      <Route path="/prototype/owner-dashboard" element={<OwnerDashboardPrototype />} />
      <Route path="/owner" element={<OwnerDashboard />} />
      <Route path="/workspace" element={<WorkspacePage />} />
      <Route path="/*" element={<Gate />} />
    </Routes>
  );
}

export default App;
